import { memo, ReactElement } from 'react';
import {
  EmptyWorkboardIllustration,
  EmptyWorkboardMobileIllustration,
  IWork,
  KanbanBoard,
  stepUtils,
  useEnvironment,
  useFixedHeader,
  WorkCard,
} from '@diana/core';
import { isEqual } from 'lodash';
import { routeUrls } from '../../app/Routing/RouteUrls';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { WorkKanbanBoardProps } from './WorkboardPage.types';
import { useTranslation } from 'react-i18next';
import { WORKBOARD_PAGE_NS } from './i18n';
import { WorkboardKanbanColumn, WorkboardKanbanCustomViewColumn } from './WorkKanbanColumns';

interface IRenderWorkItem {
  item: IWork;
  showSidePeek: boolean;
  isOfflineModeEnabled?: boolean;
}

const renderWorkItem = (props: IRenderWorkItem): ReactElement => {
  const { item, showSidePeek, isOfflineModeEnabled = false } = props;
  const Card = memo<IWork>(
    (props) => {
      const environment = useEnvironment();
      return (
        <WorkCard
          href={routeUrls.work.url({ environment, workId: props.id }, {})}
          work={props}
          openInSidePeek={showSidePeek}
          isOfflineModeEnabled={isOfflineModeEnabled}
        />
      );
    },
    (prev, next) => isEqual(prev, next),
  );

  return <Card {...item} />;
};

export function WorkKanbanBoardNoItems() {
  const { t } = useTranslation(WORKBOARD_PAGE_NS);
  const { breakpoints } = useTheme();
  const isSmallScreen = useMediaQuery(breakpoints.down('md'));
  const { headerOffset } = useFixedHeader();

  return (
    <Stack
      sx={{
        py: 3,
        left: 0,
        minWidth: '100%',
        position: 'sticky',
        minHeight: `calc(100vh - ${headerOffset * 2}px)`,
        justifyContent: 'center',
      }}
    >
      <Stack alignItems="center" spacing={8}>
        {isSmallScreen ? (
          <EmptyWorkboardMobileIllustration sx={{ maxWidth: 280, height: 'auto' }} />
        ) : (
          <EmptyWorkboardIllustration sx={{ maxWidth: 835, height: 'auto' }} />
        )}
        <Stack alignItems="center" spacing={1} sx={{ maxWidth: 466, textAlign: 'center', px: 1 }}>
          <Typography fontSize="1.29rem">{t('kanbanBoard.empty.title')}</Typography>
          <Typography color="text.secondary">{t('kanbanBoard.empty.message')}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function WorkKanbanBoard({ flow, steps, showSidePeek = true }: WorkKanbanBoardProps) {
  const isFlowLicensed = flow.isLicensed ?? false;
  const isOfflineModeEnabled = flow.featureFlags?.isOfflineModeEnabled === true;

  const renderItem = (item: IWork) => {
    return renderWorkItem({ item, showSidePeek, isOfflineModeEnabled });
  };

  const columns = steps.map((step) => {
    const createOptions = stepUtils
      .allowedStepsToStartWork({ steps: step.steps, isFlowLicensed })
      .map((innerStep) => ({
        title: innerStep.displayName,
        value: innerStep.name,
      }));

    return {
      title: step.displayName,
      items: [] as IWork[],
      renderItem,
      onCreateItem: () => {},
      createOptions,
      showIcon: createOptions.length > 0,
      ColumnComponent: WorkboardKanbanColumn,
      columnComponentProps: { flow: flow, stepId: step.id, showSidePeek },
    };
  });

  const extraColumns =
    flow?.customViews?.map((customViewQuery) => {
      const { displayName } = customViewQuery;
      return {
        title: displayName,
        items: [],
        renderItem,
        onCreateItem: () => {},
        createOptions: [],
        showIcon: false,
        ColumnComponent: WorkboardKanbanCustomViewColumn,
        columnComponentProps: { flow: flow, customView: customViewQuery, showSidePeek },
      };
    }) ?? [];

  return <KanbanBoard columns={[...columns, ...extraColumns]} detectEmpty={false} />;
}
